import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injector, runInInjectionContext, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { of, type Observable } from 'rxjs';
import type {
  FetchResource,
  ItemResource,
  ItemResourceOptions,
  ListMeta,
  ListResponse,
  ListFilter,
  ListResource,
  ListResourceOptions,
  ListSearch,
  ListSort,
  MutationResource,
  QueryParamValue,
  ResourceFactory,
} from './resource.types';

/**
 * Configuration for creating a resource factory
 */
export interface ResourceFactoryConfig {
  /** Base API path (e.g., '/api/v1/core/skills') */
  basePath: string;
  /** Default take value (default: 50) */
  defaultTake?: number;
  /** Maximum take value (default: 200) */
  maxTake?: number;
  /**
   * Optional query string appended to single-item GET requests only
   * (e.g., `'?include=translations'`). Has no effect on list or mutation
   * requests, so list and edit facades can share the same factory.
   */
  itemSuffix?: string;
}

/**
 * Type guard to check if options are for item resource
 */
function isItemResourceOptions(options?: ListResourceOptions | ItemResourceOptions): options is ItemResourceOptions {
  return options !== undefined && 'id' in options;
}

/**
 * Creates a FetchResource from an Observable.
 * Immediately subscribes and tracks loading/error/value state via signals.
 */
function createFetchResource<T>(observable: Observable<T>): FetchResource<T> {
  const _value = signal<T | null>(null);
  const _isLoading = signal(true);
  const _error = signal<Error | undefined>(undefined);

  observable.subscribe({
    next: result => {
      _value.set(result);
      _isLoading.set(false);
    },
    error: (err: HttpErrorResponse | Error) => {
      const error = err instanceof Error ? err : new Error(err.message || 'Request failed');
      _error.set(error);
      _isLoading.set(false);
    },
  });

  return {
    value: _value.asReadonly(),
    isLoading: _isLoading.asReadonly(),
    error: _error.asReadonly(),
    isLoaded: computed(() => !_isLoading()),
  };
}

/**
 * Creates a MutationResource from an Observable.
 * Immediately subscribes and tracks loading/error/value/success state via signals.
 */
export function createMutationResource<T>(observable: Observable<T>): MutationResource<T> {
  const _value = signal<T | null>(null);
  const _isLoading = signal(true);
  const _error = signal<Error | undefined>(undefined);
  const _isSuccess = signal(false);

  observable.subscribe({
    next: result => {
      _value.set(result);
      _isLoading.set(false);
      _isSuccess.set(true);
    },
    error: (err: HttpErrorResponse | Error) => {
      // Store the original error without wrapping so callers can inspect
      // instanceof HttpErrorResponse (which implements but does not extend Error)
      _error.set(err as Error);
      _isLoading.set(false);
      _isSuccess.set(false);
    },
  });

  return {
    value: _value.asReadonly(),
    isLoading: _isLoading.asReadonly(),
    error: _error.asReadonly(),
    isComplete: computed(() => !_isLoading()),
    isSuccess: _isSuccess.asReadonly(),
  };
}

function clampTake(take: number, maxTake: number): number {
  if (!Number.isFinite(take) || take < 1) {
    return 1;
  }
  return Math.min(Math.trunc(take), maxTake);
}

function normalizeSkip(skip: number): number {
  if (!Number.isFinite(skip) || skip < 0) {
    return 0;
  }
  return Math.trunc(skip);
}

function encodeQueryParamValue(value: QueryParamValue): string {
  if (Array.isArray(value)) {
    return value.map(String).join(',');
  }
  return String(value);
}

function toFilterExpression(filter: ListFilter): string {
  const value = filter.value;
  if (value === undefined) {
    return `${filter.field}:${filter.operator}:`;
  }
  if (Array.isArray(value)) {
    return `${filter.field}:${filter.operator}:${value.map(String).join(',')}`;
  }
  return `${filter.field}:${filter.operator}:${String(value)}`;
}

function toSortExpression(sort: ListSort): string {
  return `${sort.field}:${sort.direction}`;
}

function toSearchExpression(search: ListSearch): string {
  return `${search.field}:${search.term}`;
}

function incrementValue(v: number): number {
  return v + 1;
}

function patchParamsUpdater(
  current: Record<string, QueryParamValue>,
  params: Record<string, QueryParamValue | undefined>
): Record<string, QueryParamValue> {
  const next = { ...current };
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      delete next[key];
      continue;
    }
    next[key] = value;
  }
  return next;
}

/**
 * Returns an updater function for patchParams.
 * Extracted to reduce function nesting depth inside createListResource.
 */
function patchParamsUpdaterWith(
  params: Record<string, QueryParamValue | undefined>
): (current: Record<string, QueryParamValue>) => Record<string, QueryParamValue> {
  return current => patchParamsUpdater(current, params);
}

/**
 * Builds URL search params from the resource query parameters.
 * Extracted to module level to reduce function nesting depth.
 */
function buildQueryUrl(
  basePath: string,
  params: {
    take: number;
    skip: number;
    sort: ListSort[];
    filter: ListFilter[];
    search: ListSearch[];
    params: Record<string, QueryParamValue>;
  }
): string {
  const queryParams = new URLSearchParams();
  queryParams.set('take', String(params.take));
  queryParams.set('skip', String(params.skip));

  for (const sort of params.sort) {
    queryParams.append('sort', toSortExpression(sort));
  }

  for (const filter of params.filter) {
    queryParams.append('filter', toFilterExpression(filter));
  }

  for (const search of params.search) {
    queryParams.append('search', toSearchExpression(search));
  }

  for (const [key, value] of Object.entries(params.params)) {
    queryParams.set(key, encodeQueryParamValue(value));
  }

  return `${basePath}?${queryParams}`;
}

/**
 * Creates a resource factory for a domain entity.
 *
 * Each call to the factory creates an independent resource instance,
 * ensuring no state interference between different consumers.
 *
 * This function must be called within an Angular injection context
 * (constructor, field initializer, or runInInjectionContext).
 *
 * @example
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * class ApiResourceService {
 *   readonly skills = createResourceFactory<Skill>({
 *     basePath: '/api/v1/core/skills'
 *   });
 * }
 *
 * // Usage in component
 * readonly skillsList = this.api.skills();
 * readonly singleSkill = this.api.skills({ id: this.skillId });
 * readonly fetchedSkill = this.api.skills.get('123');
 * readonly createResult = this.api.skills.create({ name: 'New' });
 * ```
 */
export function createResourceFactory<T, TCreate = Partial<T>, TUpdate = Partial<T>>(
  config: ResourceFactoryConfig
): ResourceFactory<T, TCreate, TUpdate> {
  const http = inject(HttpClient);
  const injector = inject(Injector);
  const { basePath, defaultTake = 50, maxTake = 200, itemSuffix = '' } = config;

  /**
   * Creates an independent list resource instance
   */
  function createListResource(options?: ListResourceOptions): ListResource<T> {
    return runInInjectionContext(injector, () => {
      // Each list resource has its own independent filter state
      const _take = signal(clampTake(options?.take ?? defaultTake, maxTake));
      const _skip = signal(normalizeSkip(options?.skip ?? 0));
      const _sort = signal<ListSort[]>(options?.sort ?? []);
      const _filter = signal<ListFilter[]>(options?.filter ?? []);
      const _search = signal<ListSearch[]>(options?.search ?? []);
      const _params = signal<Record<string, QueryParamValue>>(options?.params ?? {});
      const _refreshTrigger = signal(0);

      const resource = rxResource({
        params: () => ({
          take: _take(),
          skip: _skip(),
          sort: _sort(),
          filter: _filter(),
          search: _search(),
          params: _params(),
          refresh: _refreshTrigger(),
        }),
        stream: ({ params }) => {
          const url = buildQueryUrl(basePath, params);
          return http.get<ListResponse<T>>(url);
        },
        defaultValue: {
          data: [] as T[],
          meta: {
            take: defaultTake,
            skip: 0,
            count: 0,
            total: 0,
            hasMore: false,
          },
        },
      });

      const meta = computed((): ListMeta => {
        const value = resource.value();
        return (
          value?.meta ?? {
            take: _take(),
            skip: _skip(),
            count: 0,
            total: 0,
            hasMore: false,
          }
        );
      });

      return {
        value: computed(() => {
          const value = resource.value();
          return value?.data ?? [];
        }),
        meta,
        total: computed(() => meta().total),
        count: computed(() => meta().count),
        hasMore: computed(() => meta().hasMore),
        isLoading: computed(() => resource.isLoading()),
        error: computed(() => resource.error()),
        isLoaded: computed(() => {
          const status = resource.status();
          return status === 'resolved' || status === 'error';
        }),
        take: _take.asReadonly(),
        skip: _skip.asReadonly(),
        sort: _sort.asReadonly(),
        filter: _filter.asReadonly(),
        search: _search.asReadonly(),
        params: _params.asReadonly(),
        setTake: (take: number) => _take.set(clampTake(take, maxTake)),
        setSkip: (skip: number) => _skip.set(normalizeSkip(skip)),
        setWindow: (skip: number, take: number) => {
          _skip.set(normalizeSkip(skip));
          _take.set(clampTake(take, maxTake));
        },
        setSort: (sort: ListSort[]) => _sort.set(sort),
        setFilter: (filter: ListFilter[]) => {
          _filter.set(filter);
          _skip.set(0);
        },
        setSearch: (search: ListSearch[]) => {
          _search.set(search);
          _skip.set(0);
        },
        patchParams: (params: Record<string, QueryParamValue | undefined>) => _params.update(patchParamsUpdaterWith(params)),
        reload: () => _refreshTrigger.update(incrementValue),
      };
    });
  }

  /**
   * Creates an independent item resource instance (reactive)
   */
  function createItemResource(options: ItemResourceOptions): ItemResource<T> {
    return runInInjectionContext(injector, () => {
      const resource = rxResource({
        params: () => ({ id: options.id() }),
        stream: ({ params }) => {
          if (!params.id) return of(null);
          return http.get<T>(`${basePath}/${params.id}${itemSuffix}`);
        },
        defaultValue: null,
      });

      return {
        value: computed(() => resource.value()),
        isLoading: computed(() => resource.isLoading()),
        error: computed(() => resource.error()),
        isLoaded: computed(() => {
          const status = resource.status();
          return status === 'resolved' || status === 'error';
        }),
        reload: () => resource.reload(),
      };
    });
  }

  // Create the factory function with overloaded signatures
  const factory = ((options?: ListResourceOptions | ItemResourceOptions) => {
    if (isItemResourceOptions(options)) {
      return createItemResource(options);
    }
    return createListResource(options);
  }) as ResourceFactory<T, TCreate, TUpdate>;

  // Attach methods to the factory - all return resource objects with signals
  factory.get = (id: string): FetchResource<T> => createFetchResource(http.get<T>(`${basePath}/${id}`));

  factory.create = (data: TCreate): MutationResource<T> => createMutationResource(http.post<T>(basePath, data));

  factory.update = (id: string, data: TUpdate): MutationResource<T> => createMutationResource(http.patch<T>(`${basePath}/${id}`, data));

  factory.delete = (id: string): MutationResource<void> => createMutationResource(http.delete<void>(`${basePath}/${id}`));

  return factory;
}
